import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Download, X, Trash2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const err = (e) => e?.response?.data?.detail?.toString?.() || "Something went wrong";
const INTENT_CLS = { transactional: "bg-emerald-100 text-emerald-800", commercial: "bg-indigo-100 text-indigo-800", navigational: "bg-sky-100 text-sky-800", informational: "bg-slate-100 text-slate-700", unclassified: "bg-white text-slate-500 border" };

function LocationPicker({ value, onChange, disabled }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setOpts([]);
    const t = setTimeout(() => api.get("/keyword-intel/locations", { params: { q } }).then((r) => setOpts(r.data)).catch((e) => toast.error(err(e))), 400);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Input disabled={disabled} placeholder="Add location (city, state, country)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="kw-location-input" />
      {opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-testid="kw-location-options">
          {opts.map((o) => <button key={o.resource} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => { if (!value.find((v) => v.resource === o.resource)) onChange([...value, o]); setQ(""); setOpts([]); }}>{o.name} <span className="text-xs text-slate-400">{o.type}</span></button>)}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-1">{value.map((v) => <Badge key={v.resource} variant="outline" className="gap-1">{v.name}<X className="w-3 h-3 cursor-pointer" onClick={() => onChange(value.filter((x) => x !== v))} /></Badge>)}</div>
    </div>
  );
}

function SearchForm({ ready, onDone }) {
  const [f, setF] = useState({ keywords: "", url: "", locs: [], language: "any", network: "GOOGLE_SEARCH", limit: 200, save_as: "" });
  const [langs, setLangs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ready) api.get("/keyword-intel/languages").then((r) => setLangs(r.data)).catch(() => {}); }, [ready]);
  const run = async (e) => {
    e.preventDefault();
    const keywords = f.keywords.split(/[\n,]/).map((k) => k.trim()).filter(Boolean);
    if (!keywords.length && !f.url.trim()) return toast.error("Enter keywords or a website");
    setBusy(true);
    try {
      const { data } = await api.post("/keyword-intel/ideas", { keywords, url: f.url || null, geo_targets: f.locs.map((l) => l.resource), language: f.language === "any" ? null : f.language, network: f.network, limit: Number(f.limit), save_as: f.save_as || null });
      toast.success(`${data.result_count} keyword ideas`); onDone(data);
    } catch (e2) { toast.error(err(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={run} className="grid md:grid-cols-2 gap-3" data-testid="kw-form">
      <Textarea disabled={!ready} rows={3} placeholder="Seed keywords, one per line or comma separated (max 20)" value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} data-testid="kw-keywords-input" />
      <div className="space-y-2">
        <Input disabled={!ready} placeholder="Or a website / landing page URL (optional)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="kw-url-input" />
        <LocationPicker disabled={!ready} value={f.locs} onChange={(locs) => setF({ ...f, locs })} />
      </div>
      <div className="flex flex-wrap gap-2 md:col-span-2">
        <Select disabled={!ready} value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
          <SelectTrigger className="w-48" data-testid="kw-language-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="any">All languages</SelectItem>{langs.map((l) => <SelectItem key={l.resource} value={l.resource}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select disabled={!ready} value={f.network} onValueChange={(v) => setF({ ...f, network: v })}>
          <SelectTrigger className="w-56" data-testid="kw-network-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="GOOGLE_SEARCH">Google Search</SelectItem><SelectItem value="GOOGLE_SEARCH_AND_PARTNERS">Search + partners</SelectItem></SelectContent>
        </Select>
        <Input disabled={!ready} type="number" min={1} max={1000} value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value })} className="w-24" data-testid="kw-limit-input" />
        <Input disabled={!ready} placeholder="Save as (optional name)" value={f.save_as} onChange={(e) => setF({ ...f, save_as: e.target.value })} className="w-56" data-testid="kw-saveas-input" />
        <Button type="submit" disabled={!ready || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kw-search-button">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Get keyword ideas</Button>
      </div>
    </form>
  );
}

function Spark({ hist }) {
  if (!hist?.length) return <span className="text-slate-300">—</span>;
  const max = Math.max(...hist.map((h) => h.searches), 1);
  return <div className="flex items-end gap-px h-6" title={hist.map((h) => `${h.month?.slice(0, 3)} ${h.year}: ${h.searches}`).join("\n")}>{hist.map((h, i) => <div key={i} className="w-1 bg-indigo-400 rounded-sm" style={{ height: `${Math.max(8, (h.searches / max) * 100)}%` }} />)}</div>;
}

const COLS = [["keyword", "Keyword"], ["avg_monthly_searches", "Avg monthly searches"], ["competition_index", "Competition"], ["low_top_of_page_bid", "Top-of-page bid (low)"], ["high_top_of_page_bid", "Top-of-page bid (high)"], ["intent", "Buyer intent"]];

function Results({ rec }) {
  const [sort, setSort] = useState(["avg_monthly_searches", -1]);
  const [intent, setIntent] = useState("all");
  const [minVol, setMinVol] = useState("");
  const rows = useMemo(() => {
    const r = rec.results.filter((x) => (intent === "all" || x.intent === intent) && (!minVol || (x.avg_monthly_searches || 0) >= Number(minVol)));
    const [k, d] = sort;
    return [...r].sort((a, b) => ((a[k] ?? -1) > (b[k] ?? -1) ? d : -d));
  }, [rec, sort, intent, minVol]);
  const csv = () => {
    const head = ["keyword", "avg_monthly_searches", "competition", "competition_index", `low_bid_${rec.currency || ""}`, `high_bid_${rec.currency || ""}`, "intent", "intent_reason", "data_type"];
    const body = rows.map((r) => [r.keyword, r.avg_monthly_searches, r.competition, r.competition_index, r.low_top_of_page_bid, r.high_top_of_page_bid, r.intent, r.reason, rec.data_label].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const url = URL.createObjectURL(new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `keywords-${rec.id.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="kw-results">
      <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-2 justify-between">
        <div>
          <div className="font-semibold text-sm">{rec.name}</div>
          <div className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="kw-data-label"><Info className="w-3 h-3" />{rec.data_label} · not live campaign performance · bids in {rec.currency || "account currency"}</div>
        </div>
        <div className="flex gap-2">
          <Select value={intent} onValueChange={setIntent}><SelectTrigger className="w-44 h-8" data-testid="kw-intent-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All intents</SelectItem>{Object.keys(INTENT_CLS).map((i) => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}</SelectContent></Select>
          <Input type="number" placeholder="Min searches" value={minVol} onChange={(e) => setMinVol(e.target.value)} className="w-32 h-8" data-testid="kw-minvol-input" />
          <Button size="sm" variant="outline" onClick={csv} data-testid="kw-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-xs text-slate-600 sticky top-0"><tr>
            {COLS.map(([k, l]) => <th key={k} className="p-2 text-left cursor-pointer select-none" onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])} data-testid={`kw-sort-${k}`}>{l}{sort[0] === k ? (sort[1] < 0 ? " ↓" : " ↑") : ""}</th>)}
            <th className="p-2 text-left">12-month trend</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.keyword} className="border-t border-slate-100" data-testid="kw-row">
              <td className="p-2 font-medium">{r.keyword}</td>
              <td className="p-2">{r.avg_monthly_searches?.toLocaleString() ?? "—"}</td>
              <td className="p-2 text-xs">{r.competition || "—"}{r.competition_index != null ? ` (${r.competition_index})` : ""}</td>
              <td className="p-2">{r.low_top_of_page_bid ?? "—"}</td><td className="p-2">{r.high_top_of_page_bid ?? "—"}</td>
              <td className="p-2"><Badge className={`${INTENT_CLS[r.intent]} capitalize`} title={r.reason}>{r.intent}</Badge><div className="text-[10px] text-slate-400">{r.reason}</div></td>
              <td className="p-2"><Spark hist={r.monthly_history} /></td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="kw-results-empty">No keywords match these filters.</div>}
      </div>
    </Card>
  );
}

export default function KeywordResearch() {
  const [st, setSt] = useState(null);
  const [saved, setSaved] = useState([]);
  const [rec, setRec] = useState(null);
  const loadSaved = () => api.get("/keyword-intel/research").then((r) => setSaved(r.data));
  useEffect(() => { api.get("/keyword-intel/status").then((r) => setSt(r.data)); loadSaved(); }, []);
  const ready = st?.access?.company_enabled && st?.access?.platform_ready;
  const open = (id) => api.get(`/keyword-intel/research/${id}`).then((r) => setRec(r.data)).catch((e) => toast.error(err(e)));
  const del = async (id) => { await api.delete(`/keyword-intel/research/${id}`); if (rec?.id === id) setRec(null); loadSaved(); };
  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="keyword-research-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Keyword Research</h1>
        <p className="text-sm text-slate-500">Search demand, competition and bid estimates from Google Keyword Planner, with buyer-intent tags.</p>
      </div>
      {st && !ready && (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="kw-not-connected">
          {!st.access.company_enabled ? "Keyword Research is disabled for your company. Contact WaggleAI support." : "Not connected — WaggleAI support has not connected the Google Ads API yet. No sample data is shown."}
        </div>
      )}
      {st && <div className="text-xs text-slate-500" data-testid="kw-usage">Requests this month: {st.access.used_this_month}{st.access.monthly_limit != null ? ` / ${st.access.monthly_limit}` : " · unlimited"} · Device split: {st.device_breakdown}</div>}
      <Card className="p-4 border-slate-200"><SearchForm ready={ready} onDone={(r) => { setRec(r); loadSaved(); }} /></Card>
      <div className="grid lg:grid-cols-[1fr_260px] gap-5 items-start">
        {rec ? <Results rec={rec} /> : <Card className="p-10 text-center text-sm text-slate-500 border-slate-200" data-testid="kw-empty">Run a search or open saved research.</Card>}
        <Card className="p-3 border-slate-200" data-testid="kw-saved">
          <div className="text-sm font-semibold mb-2">Saved research</div>
          {saved.length === 0 ? <p className="text-xs text-slate-500">Nothing saved yet.</p> : saved.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 text-xs">
              <button className="text-left text-indigo-700 hover:underline truncate" onClick={() => open(s.id)} data-testid={`kw-saved-${s.id}`}>{s.name}<span className="block text-slate-400">{s.result_count} keywords · {new Date(s.created_at).toLocaleDateString()}</span></button>
              <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600 cursor-pointer shrink-0" onClick={() => del(s.id)} data-testid={`kw-saved-delete-${s.id}`} />
            </div>))}
        </Card>
      </div>
    </div>
  );
}
